import { useEffect, useMemo, useState } from 'react';
import { ArrowRight, BusFront, RefreshCw } from 'lucide-react';
import { Chrome } from '@/components/Chrome';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SURFACES } from '@/lib/gql/__generated__/surfaces';
import { useSurfaceAccess } from '@/lib/platform/surfaces';
import type { AvailableSchedule } from '@/features/schedule/state/schedule.actors';
import { useSchedule, useScheduleRef } from '@/features/schedule/state';
import { useSelection, useSelectionRef } from './state';

type ScheduleRecord = {
  route?: string | null;
  direction?: string | null;
  stopId?: string | null;
  stopName?: string | null;
  scheduledTime?: string | null;
  tripDestination?: string | null;
};

type ValidSchedule = AvailableSchedule & {
  route: string;
  direction: string;
  stopId: string;
  stopName: string;
};

type SelectionView = {
  route?: string | null;
  direction?: string | null;
  stopName?: string | null;
  missingCoverage?: boolean | null;
  nextScheduledBuses?: Array<ScheduleRecord | null> | null;
};

const validSchedule = (schedule: AvailableSchedule): schedule is ValidSchedule =>
  Boolean(schedule.route && schedule.direction && schedule.stopId && schedule.stopName);

function formatScheduledTime(value: string | null | undefined): string {
  if (!value) return 'Time not available';
  const parsed = new Date(value);
  if (!Number.isNaN(parsed.getTime()) && value.includes('T')) {
    return new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' }).format(parsed);
  }
  return value;
}

function Notice({ children, tone = 'muted' }: { children: string; tone?: 'muted' | 'error' }) {
  return (
    <div
      className={
        tone === 'error'
          ? 'rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive'
          : 'rounded-md border border-border bg-muted px-4 py-3 text-sm text-muted-foreground'
      }
      role={tone === 'error' ? 'alert' : undefined}
    >
      {children}
    </div>
  );
}

function SelectField({
  id,
  label,
  value,
  placeholder,
  items,
  onValueChange,
  disabled = false,
  dataJrKey,
}: {
  id: string;
  label: string;
  value: string;
  placeholder: string;
  items: Array<{ value: string; label: string }>;
  onValueChange: (value: string) => void;
  disabled?: boolean;
  dataJrKey: string;
}) {
  return (
    <div className="grid gap-2">
      <label htmlFor={id} className="text-sm font-medium">
        {label}
      </label>
      <Select
        value={value || null}
        items={items}
        onValueChange={(nextValue) => {
          if (typeof nextValue === 'string') onValueChange(nextValue);
        }}
        disabled={disabled || items.length === 0}
      >
        <SelectTrigger id={id} data-jr-key={dataJrKey} className="min-h-11 w-full">
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {items.map((item) => (
            <SelectItem key={item.value} value={item.value}>
              {item.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function ScheduleChooser() {
  const scheduleSnapshot = useSchedule((snapshot) => snapshot);
  const selectionRef = useSelectionRef();
  const [route, setRoute] = useState('');
  const [direction, setDirection] = useState('');
  const [stopId, setStopId] = useState('');

  const schedules = scheduleSnapshot.context.schedules;
  const records = useMemo<ValidSchedule[]>(() => schedules.filter(validSchedule), [schedules]);
  const routes = useMemo(
    () => [...new Set(records.map((schedule) => schedule.route))].sort().map((value) => ({ value, label: value })),
    [records],
  );
  const directions = useMemo(
    () =>
      [...new Set(records.filter((schedule) => schedule.route === route).map((schedule) => schedule.direction))]
        .sort()
        .map((value) => ({ value, label: value })),
    [records, route],
  );
  const stops = useMemo(
    () =>
      Array.from(
        new Map<string, string>(
          records
            .filter((schedule) => schedule.route === route && schedule.direction === direction)
            .map((schedule) => [schedule.stopId, schedule.stopName]),
        ),
      )
        .sort(([, first], [, second]) => first.localeCompare(second))
        .map(([value, label]) => ({ value, label })),
    [records, route, direction],
  );
  const stopName = stops.find((stop) => stop.value === stopId)?.label ?? '';
  const selectionSnapshot = useSelection((snapshot) => snapshot);
  const saving = selectionSnapshot.matches({ write: 'saving' });

  useEffect(() => {
    if (scheduleSnapshot.matches({ available: 'ready' }) && !route && routes[0]) {
      setRoute(routes[0].value);
    }
  }, [route, routes, scheduleSnapshot]);

  useEffect(() => {
    if (!directions.some((item) => item.value === direction)) setDirection('');
  }, [direction, directions]);

  useEffect(() => {
    if (!stops.some((item) => item.value === stopId)) setStopId('');
  }, [stopId, stops]);

  if (scheduleSnapshot.matches({ available: 'loading' })) return <Notice>Loading available routes and stops…</Notice>;
  if (scheduleSnapshot.matches({ available: 'error' })) {
    return <Notice tone="error">Available routes and stops could not be loaded. Please try again later.</Notice>;
  }
  if (records.length === 0) return <Notice>No published schedule choices are available yet.</Notice>;

  const canChoose = Boolean(route && direction && stopId && stopName) && !saving;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Choose a stop</CardTitle>
        <CardDescription>Pick from published Seattle schedule data to see the next buses.</CardDescription>
      </CardHeader>
      <CardContent className="grid gap-5">
        <SelectField
          id="choose-route"
          label="Route"
          value={route}
          placeholder="Select a route"
          items={routes}
          dataJrKey="choose-selection-route"
          onValueChange={(value) => {
            setRoute(value);
            setDirection('');
            setStopId('');
          }}
        />
        <SelectField
          id="choose-direction"
          label="Direction"
          value={direction}
          placeholder="Select a direction"
          items={directions}
          disabled={!route}
          dataJrKey="choose-selection-direction"
          onValueChange={(value) => {
            setDirection(value);
            setStopId('');
          }}
        />
        <SelectField
          id="choose-stop"
          label="Stop"
          value={stopId}
          placeholder="Select a stop"
          items={stops}
          disabled={!direction}
          dataJrKey="choose-selection-stopId"
          onValueChange={setStopId}
        />
        <SelectField
          id="choose-stop-name"
          label="Stop name"
          value={stopName}
          placeholder="Select a published stop"
          items={stops.map((stop) => ({ value: stop.label, label: stop.label }))}
          disabled={!direction}
          dataJrKey="choose-selection-stopName"
          onValueChange={(value) => setStopId(stops.find((stop) => stop.label === value)?.value ?? '')}
        />
        {selectionSnapshot.context.message && <Notice>{selectionSnapshot.context.message}</Notice>}
        <Button
          type="button"
          size="lg"
          className="min-h-11 w-full sm:w-fit"
          data-jr-key="choose-selection"
          disabled={!canChoose}
          onClick={() => {
            selectionRef.send({
              type: 'selection.choose',
              input: { route, direction, stopId, stopName },
            });
          }}
        >
          Show bus times
          <ArrowRight />
        </Button>
      </CardContent>
    </Card>
  );
}

function Results({ view, label }: { view: SelectionView; label: string }) {
  const buses = (view.nextScheduledBuses ?? []).filter((bus): bus is ScheduleRecord =>
    Boolean(bus?.scheduledTime || bus?.tripDestination),
  );
  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle>{label}</CardTitle>
            <CardDescription>
              {view.route} · {view.direction}
            </CardDescription>
          </div>
          <Badge variant="secondary">Scheduled times</Badge>
        </div>
      </CardHeader>
      <CardContent className="grid gap-4">
        <div className="rounded-md border border-border bg-muted/50 p-4">
          <p className="text-xs font-medium tracking-wide text-muted-foreground uppercase">Selected stop</p>
          <p className="mt-1 font-heading text-xl font-semibold">{view.stopName ?? 'Stop name unavailable'}</p>
        </div>
        {view.missingCoverage && (
          <Notice>
            Some scheduled coverage is missing for this stop. Times shown below are the available schedule.
          </Notice>
        )}
        {buses.length > 0 ? (
          <div className="grid gap-3 sm:grid-cols-3">
            {buses.slice(0, 3).map((bus, index) => (
              <div key={`${bus.scheduledTime ?? 'bus'}-${index}`} className="rounded-md border border-border p-4">
                <p className="font-heading text-2xl font-semibold text-primary">
                  {formatScheduledTime(bus.scheduledTime)}
                </p>
                <p className="mt-1 text-sm text-muted-foreground">{bus.tripDestination ?? 'Destination not listed'}</p>
              </div>
            ))}
          </div>
        ) : (
          <Notice>No upcoming scheduled buses are available for this selection.</Notice>
        )}
      </CardContent>
    </Card>
  );
}

function RiderContent() {
  const access = useSurfaceAccess(SURFACES['bus-times']);
  const scheduleRef = useScheduleRef();
  const selectionRef = useSelectionRef();
  const next = useSelection((snapshot) => snapshot.context.next);
  const refreshed = useSelection((snapshot) => snapshot.context.refreshed);
  const nextLoading = useSelection((snapshot) => snapshot.matches({ next: 'loading' }));
  const nextReady = useSelection((snapshot) => snapshot.matches({ next: 'ready' }));
  const nextError = useSelection((snapshot) => snapshot.matches({ next: 'error' }));
  const refreshLoading = useSelection((snapshot) => snapshot.matches({ refresh: 'loading' }));
  const refreshReady = useSelection((snapshot) => snapshot.matches({ refresh: 'ready' }));
  const refreshError = useSelection((snapshot) => snapshot.matches({ refresh: 'error' }));
  const saving = useSelection((snapshot) => snapshot.matches({ write: 'saving' }));
  const hasSelection = Boolean(next?.route && next.direction && next.stopName);

  useEffect(() => {
    if (access !== 'allowed') return;
    scheduleRef.send({ type: 'schedule.load-available' });
    selectionRef.send({ type: 'selection.load-next' });
  }, [access, scheduleRef, selectionRef]);

  if (access === 'waiting') return <Notice>Checking access to bus times…</Notice>;
  if (access === 'unavailable') return <Notice tone="error">Bus times are temporarily unavailable.</Notice>;
  if (access === 'denied') return <Notice tone="error">You do not have access to this screen.</Notice>;

  return (
    <div className="grid gap-8">
      <section className="grid gap-3">
        <Badge variant="outline" className="w-fit">
          <BusFront /> Rider view
        </Badge>
        <h1 className="font-heading text-3xl font-semibold tracking-tight sm:text-4xl">Seattle bus times</h1>
        <p className="max-w-2xl text-base text-muted-foreground sm:text-lg">
          Choose a published route and stop to check the next scheduled buses.
        </p>
      </section>

      <ScheduleChooser />

      <section className="grid gap-4" aria-live="polite">
        {nextLoading && <Notice>Loading your selected stop…</Notice>}
        {nextError && <Notice tone="error">Your selected stop could not be loaded. Please choose it again.</Notice>}
        {nextReady && next && hasSelection ? (
          <Results view={next} label="Next scheduled buses" />
        ) : (
          nextReady && <Notice>Choose a route, direction, and stop to see scheduled buses.</Notice>
        )}
        {nextReady && next && hasSelection && (
          <Card>
            <CardHeader>
              <CardTitle>Refresh scheduled buses</CardTitle>
              <CardDescription>Check the latest schedule view for the same stop.</CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4">
              <Button
                type="button"
                variant="outline"
                size="lg"
                className="min-h-11 w-full sm:w-fit"
                data-jr-key="refresh-scheduled-buses"
                disabled={refreshLoading || saving}
                onClick={() => selectionRef.send({ type: 'selection.load-refresh' })}
              >
                <RefreshCw className={refreshLoading ? 'animate-spin' : undefined} />
                Refresh times
              </Button>
              {refreshLoading && <Notice>Refreshing scheduled buses…</Notice>}
              {refreshError && <Notice tone="error">Scheduled buses could not be refreshed.</Notice>}
              {refreshReady && refreshed && <Results view={refreshed} label="Refreshed scheduled buses" />}
            </CardContent>
          </Card>
        )}
      </section>
    </div>
  );
}

export function BusTimesPage() {
  return (
    <Chrome>
      <RiderContent />
    </Chrome>
  );
}
